import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { AuthProvider } from "@/hooks/use-auth";
import { ThemeProvider } from "@/hooks/use-theme";
import { ScrollToTop } from "@/components/ScrollToTop";
import { lazy, Suspense, useEffect } from "react";
import { installClientErrorReporter } from "@/lib/client-error-reporter";

const AccessibilityPanel = lazy(() =>
  import("@/components/AccessibilityPanel").then((m) => ({ default: m.AccessibilityPanel })),
);


import appCss from "../styles.css?url";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error: rawError, reset }: import("@tanstack/react-router").ErrorComponentProps) {
  const error = rawError as Error;
  console.error("[Route error]", error);
  const router = useRouter();
  const route = typeof window !== "undefined" ? window.location.pathname + window.location.search : "—";
  const timestamp = new Date().toISOString();

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-10">
      <div className="w-full max-w-xl text-left">
        <p className="mb-2 text-[0.7rem] font-semibold uppercase tracking-[0.18em] text-[color:var(--color-editorial-accent,hsl(var(--primary)))]">
          Tiger's Eye
        </p>
        <h1 className="font-serif text-2xl font-medium tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>

        <details className="mt-8 rounded-md border border-border bg-muted/40 p-4 text-xs text-muted-foreground">
          <summary className="cursor-pointer select-none text-[0.7rem] font-semibold uppercase tracking-[0.12em] text-foreground">
            Diagnostic details
          </summary>
          <dl className="mt-3 grid gap-1.5 sm:grid-cols-[8rem_1fr]">
            <dt className="text-[0.7rem] uppercase tracking-[0.08em] text-muted-foreground">Timestamp</dt>
            <dd className="font-mono text-[0.75rem] text-foreground">{timestamp}</dd>
            <dt className="text-[0.7rem] uppercase tracking-[0.08em] text-muted-foreground">Route</dt>
            <dd className="font-mono text-[0.75rem] text-foreground break-all">{route}</dd>
            <dt className="text-[0.7rem] uppercase tracking-[0.08em] text-muted-foreground">Error</dt>
            <dd className="font-mono text-[0.75rem] text-foreground break-all">
              {error.name}: {error.message}
            </dd>
          </dl>
          {error.stack && (
            <pre className="mt-3 max-h-72 overflow-auto whitespace-pre-wrap break-words rounded border border-border bg-background/60 p-3 font-mono text-[0.7rem] leading-relaxed text-destructive">
              {error.stack}
            </pre>
          )}
        </details>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "color-scheme", content: "light dark" },
      { title: "Tiger's Eye — Honest essay feedback before you submit" },
      { name: "description", content: "Honest, impartial critique of the essay you've already written. Structured, provenance-rich feedback — never ghostwriting." },
      { name: "author", content: "Tiger's Eye" },
      { name: "google-site-verification", content: "jwIto7f3cenBl5mshhAKZg79_9tvOqm7WRETiie4yp0" },
      { property: "og:title", content: "Tiger's Eye — Honest essay feedback before you submit" },
      { property: "og:description", content: "Honest, impartial critique of the essay you've already written. Structured, provenance-rich feedback — never ghostwriting." },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Tiger's Eye" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [
      { rel: "icon", type: "image/svg+xml", href: "/favicon.svg" },
      {
        rel: "stylesheet",
        href: appCss,
        fetchpriority: "high",
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              "@id": "https://tigerseye.app/#organization",
              name: "Tiger's Eye",
              url: "https://tigerseye.app",
              email: "hello@tigerseye.app",
              description:
                "Honest, critique-only academic draft review. No ghostwriting — structured, provenance-rich feedback so students can revise with confidence before submitting.",
            },
            {
              "@type": "WebSite",
              "@id": "https://tigerseye.app/#website",
              url: "https://tigerseye.app",
              name: "Tiger's Eye",
              publisher: { "@id": "https://tigerseye.app/#organization" },
              inLanguage: "en-GB",
            },
          ],
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  useEffect(() => {
    installClientErrorReporter();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ThemeProvider>
          <Outlet />
          <ScrollToTop />
          <Suspense fallback={null}>
            <AccessibilityPanel />
          </Suspense>
        </ThemeProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

